import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, TextField, Button, Grid, Divider, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions, Chip, CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getProfile, updateProfile, deactivateAccount } from '../../services/auth';
import { useApp } from '../../context/AppContext';

// Busca endereço pelo CEP usando a API pública ViaCEP (sem necessidade de chave).
async function fetchCep(cep) {
    const onlyDigits = String(cep).replace(/\D/g, '');
    if (onlyDigits.length !== 8) return null;
    const res = await fetch(`https://viacep.com.br/ws/${onlyDigits}/json/`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.erro) return null;
    return data;
}

export default function Account() {
    const navigate = useNavigate();
    const { profile, setProfile, logout } = useApp();
    const [fullName, setFullName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [cep, setCep] = useState('');
    const [logradouro, setLogradouro] = useState('');
    const [numero, setNumero] = useState('');
    const [complemento, setComplemento] = useState('');
    const [bairro, setBairro] = useState('');
    const [cidade, setCidade] = useState('');
    const [estado, setEstado] = useState('');
    const [loadingCep, setLoadingCep] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        getProfile().then(res => {
            const u = res.user;
            setProfile(u);
            setFullName(u.fullName || '');
            setBirthDate(u.birthDate ? u.birthDate.slice(0, 10) : '');
            const a = u.address || {};
            setCep(a.cep || '');
            setLogradouro(a.logradouro || '');
            setNumero(a.numero || '');
            setComplemento(a.complemento || '');
            setBairro(a.bairro || '');
            setCidade(a.cidade || '');
            setEstado(a.estado || '');
        }).catch(() => {});
    }, []);

    const handleCepChange = async (e) => {
        const value = e.target.value;
        setCep(value);
        const digits = value.replace(/\D/g, '');
        if (digits.length === 8) {
            setLoadingCep(true);
            try {
                const data = await fetchCep(digits);
                if (data) {
                    setLogradouro(data.logradouro || '');
                    setBairro(data.bairro || '');
                    setCidade(data.localidade || '');
                    setEstado(data.uf || '');
                } else {
                    toast.error('CEP não encontrado');
                }
            } catch (err) {
                toast.error('Erro ao buscar CEP');
            } finally {
                setLoadingCep(false);
            }
        }
    };

    const handleSave = async () => {
        if (!fullName.trim()) return toast.error('Nome completo é obrigatório');
        if (!birthDate) return toast.error('Data de nascimento é obrigatória');
        if (!cep.trim()) return toast.error('CEP é obrigatório');
        if (!numero.trim()) return toast.error('Número é obrigatório');

        setLoading(true);
        try {
            const res = await updateProfile({
                fullName: fullName.trim(),
                birthDate,
                address: {
                    cep: cep.trim(),
                    logradouro: logradouro.trim(),
                    numero: numero.trim(),
                    complemento: complemento.trim(),
                    bairro: bairro.trim(),
                    cidade: cidade.trim(),
                    estado: estado.trim()
                }
            });
            setProfile(res.user);
            toast.success('Perfil atualizado!');
        } catch (e) {
            toast.error(e.response?.data?.error || 'Erro ao salvar');
        } finally { setLoading(false); }
    };

    const handleDeactivate = async () => {
        try {
            await deactivateAccount();
            toast.success('Conta desativada e dados removidos.');
            logout();
            navigate('/login');
        } catch (e) {
            toast.error(e.response?.data?.error || 'Erro ao desativar conta');
        } finally { setConfirmOpen(false); }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 3, maxWidth: 720, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom>Configurações de Conta</Typography>

            <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">E-mail</Typography>
                        <Typography>{profile?.email || '—'}</Typography>
                        <Chip
                            size="small"
                            sx={{ mt: 1 }}
                            color={profile?.verified ? 'success' : 'warning'}
                            label={profile?.verified ? 'Verificado' : 'Não verificado'}
                        />
                        {profile?.authMethod === 'google' && (
                            <Chip size="small" sx={{ mt: 1, ml: 1 }} label="Google" />
                        )}
                    </Grid>

                    <Grid item xs={12} sm={8}>
                        <TextField label="Nome completo *" fullWidth value={fullName}
                            onChange={(e) => setFullName(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Data de nascimento *" type="date" fullWidth
                            InputLabelProps={{ shrink: true }} value={birthDate}
                            onChange={(e) => setBirthDate(e.target.value)} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField label="CEP *" fullWidth value={cep}
                            onChange={handleCepChange}
                            InputProps={{
                                endAdornment: loadingCep ? <CircularProgress size={18} /> : null
                            }}
                            placeholder="00000-000" />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField label="Logradouro" fullWidth value={logradouro}
                            onChange={(e) => setLogradouro(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField label="Número *" fullWidth value={numero}
                            onChange={(e) => setNumero(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        <TextField label="Complemento" fullWidth value={complemento}
                            onChange={(e) => setComplemento(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField label="Bairro" fullWidth value={bairro}
                            onChange={(e) => setBairro(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField label="Cidade" fullWidth value={cidade}
                            onChange={(e) => setCidade(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField label="UF" fullWidth value={estado}
                            onChange={(e) => setEstado(e.target.value)} />
                    </Grid>

                    <Grid item xs={12}>
                        <Button variant="contained" onClick={handleSave} disabled={loading}>
                            Salvar alterações
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Divider sx={{ my: 4 }} />

            <Paper sx={{ p: 3, border: '1px solid', borderColor: 'error.main' }}>
                <Typography variant="h6" color="error" gutterBottom>Zona de perigo</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Ao desativar a conta, todos os seus dados (dispositivos, plantas, leituras e histórico)
                    serão permanentemente removidos do banco de dados. Esta ação não pode ser desfeita.
                </Typography>
                <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
                    Desativar e excluir conta
                </Button>
            </Paper>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Desativar conta?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Todos os seus dados serão excluídos permanentemente. Deseja continuar?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeactivate} color="error">Excluir conta</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
